import { useState, useEffect } from 'preact/hooks'
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-preact'

export interface ToastMessage {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number // in milliseconds, default 5000
}

interface ToastContainerProps {
  toasts: ToastMessage[]
  onRemoveToast: (id: string) => void
}

interface ToastProps {
  toast: ToastMessage
  onRemove: (id: string) => void
}

function Toast({ toast, onRemove }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Trigger enter animation
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (toast.duration !== 0) {
      const duration = toast.duration || 5000
      const timer = setTimeout(() => {
        handleRemove()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [toast.duration])

  const handleRemove = (e?: Event) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setIsExiting(true)
    setTimeout(() => onRemove(toast.id), 300) // Match exit animation duration
  }

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />
      case 'error':
        return <AlertCircle className="w-5 h-5" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />
      case 'info':
        return <Info className="w-5 h-5" />
    }
  }

  const getColors = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-primary-100 dark:bg-primary-900/80',
          border: 'border-primary-300 dark:border-primary-600',
          icon: 'text-primary-700 dark:text-primary-300',
          title: 'text-primary-900 dark:text-primary-100',
          message: 'text-primary-800 dark:text-primary-200',
          button: 'text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200'
        }
      case 'error':
        return {
          bg: 'bg-red-100 dark:bg-red-900/80',
          border: 'border-red-300 dark:border-red-600',
          icon: 'text-red-700 dark:text-red-300',
          title: 'text-red-900 dark:text-red-100',
          message: 'text-red-800 dark:text-red-200',
          button: 'text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200'
        }
      case 'warning':
        return {
          bg: 'bg-yellow-100 dark:bg-yellow-900/80',
          border: 'border-yellow-300 dark:border-yellow-600',
          icon: 'text-yellow-700 dark:text-yellow-300',
          title: 'text-yellow-900 dark:text-yellow-100',
          message: 'text-yellow-800 dark:text-yellow-200',
          button: 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-300 dark:hover:text-yellow-200'
        }
      case 'info':
        return {
          bg: 'bg-primary-100 dark:bg-primary-900/80',
          border: 'border-primary-300 dark:border-primary-600',
          icon: 'text-primary-700 dark:text-primary-300',
          title: 'text-primary-900 dark:text-primary-100',
          message: 'text-primary-800 dark:text-primary-200',
          button: 'text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200'
        }
    }
  }

  const colors = getColors()

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out mb-3
        ${isVisible && !isExiting 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
        }
      `}
    >
      <div
        className={`
          min-w-80 max-w-md w-full ${colors.bg} ${colors.border} border rounded-lg shadow-lg p-4
        `}
      >
        <div className="flex items-start">
          <div className={`flex-shrink-0 ${colors.icon} mt-0.5`}>
            {getIcon()}
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className={`text-sm font-medium ${colors.title} leading-5`}>
              {toast.title}
            </p>
            <p className={`mt-1 text-sm ${colors.message} leading-relaxed break-words`}>
              {toast.message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className={`
                inline-flex ${colors.button} focus:outline-none focus:ring-2 
                focus:ring-offset-2 focus:ring-primary-500 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5
              `}
              onClick={handleRemove}
            >
              <span className="sr-only">Close</span>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ToastContainer({ toasts, onRemoveToast }: ToastContainerProps) {
  return (
    <div
      className="fixed top-4 right-4 z-50 space-y-3 max-w-md"
      style={{ zIndex: 9999 }}
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onRemove={onRemoveToast}
        />
      ))}
    </div>
  )
}
